import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Plus, Loader2, Mail } from 'lucide-react';
import { useApiMutation, useApi } from '../../hooks/useApi';
import { tenantAPI, propertyAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AddTenantForm = ({ onClose, onSuccess, tenant = null }) => {
  const isEditMode = !!tenant;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_id: '',
    unit_number: '',
    rent_amount: '',
    lease_start: '',
    lease_end: '',
    status: 'active',
    payment_status: 'pending'
  });
  const [sendEmail, setSendEmail] = useState(true); // Default to checked

  const { data: properties } = useApi(() => propertyAPI.getAll());
  const { mutate, loading, error } = useApiMutation();
  const { toast } = useToast();

  // Set default lease dates or populate edit data
  useEffect(() => {
    if (isEditMode && tenant) {
      // Populate form with existing tenant data
      setFormData({
        name: tenant.name || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        property_id: tenant.property_id || '',
        unit_number: tenant.unit || tenant.unit_number || '',
        rent_amount: tenant.rent_amount || '',
        lease_start: tenant.lease_start ? new Date(tenant.lease_start).toISOString().split('T')[0] : '',
        lease_end: tenant.lease_end ? new Date(tenant.lease_end).toISOString().split('T')[0] : '',
        status: tenant.status || 'active',
        payment_status: tenant.payment_status || 'pending'
      });
      setSendEmail(false); // Don't send email on edit by default
    } else {
      // Set default dates for new tenant
      const today = new Date();
      const nextYear = new Date(today);
      nextYear.setFullYear(today.getFullYear() + 1);
      
      setFormData(prev => ({
        ...prev,
        lease_start: today.toISOString().split('T')[0],
        lease_end: nextYear.toISOString().split('T')[0]
      }));
    }
  }, [isEditMode, tenant]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values and format dates
      const tenantData = {
        ...formData,
        rent_amount: parseFloat(formData.rent_amount),
        lease_start: new Date(formData.lease_start).toISOString(),
        lease_end: new Date(formData.lease_end).toISOString(),
      };

      if (isEditMode) {
        // Update existing tenant
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/${tenant.id || tenant._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('landlord_token')}`
          },
          body: JSON.stringify(tenantData)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to update tenant');
        }

        toast({
          title: "Success!",
          description: `Tenant "${formData.name}" has been updated successfully.`,
        });
      } else {
        // Create new tenant
        const createdTenant = await mutate(tenantAPI.create, tenantData, sendEmail);

        const successMessage = createdTenant.email_sent 
          ? `Tenant "${createdTenant.name}" has been added successfully. Login credentials have been sent to ${formData.email}.`
          : `Tenant "${createdTenant.name}" has been added successfully.`;

        toast({
          title: "Success!",
          description: successMessage,
        });
      }

      onSuccess?.();
      onClose();

    } catch (err) {
      toast({
        title: "Error",
        description: err.message || `Failed to ${isEditMode ? 'update' : 'add'} tenant. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isEditMode ? 'Edit Tenant' : 'Add New Tenant'}</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john.smith@email.com"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                  required
                />
              </div>
              <div>
                <Label htmlFor="property_id">Property *</Label>
                <select
                  id="property_id"
                  name="property_id"
                  value={formData.property_id}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a property</option>
                  {properties?.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name} - {property.address}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="unit_number">Unit Number *</Label>
                <Input
                  id="unit_number"
                  name="unit_number"
                  value={formData.unit_number}
                  onChange={handleInputChange}
                  placeholder="e.g., A101, 2B, 301"
                  required
                />
              </div>
              <div>
                <Label htmlFor="rent_amount">Monthly Rent *</Label>
                <Input
                  id="rent_amount"
                  name="rent_amount"
                  type="number"
                  step="0.01"
                  value={formData.rent_amount}
                  onChange={handleInputChange}
                  placeholder="e.g., 1200.00"
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lease_start">Lease Start Date *</Label>
                <Input
                  id="lease_start"
                  name="lease_start"
                  type="date"
                  value={formData.lease_start}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="lease_end">Lease End Date *</Label>
                <Input
                  id="lease_end"
                  name="lease_end"
                  type="date"
                  value={formData.lease_end}
                  onChange={handleInputChange}
                  min={formData.lease_start}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Tenant Status</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending Move-in</option>
                </select>
              </div>
              <div>
                <Label htmlFor="payment_status">Payment Status</Label>
                <select
                  id="payment_status"
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Current</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Send Email Checkbox - Only show for new tenants */}
            {!isEditMode && (
              <div className="flex items-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="send_email" className="cursor-pointer text-sm">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Send login credentials to tenant's email automatically
                </Label>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Adding Tenant...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {isEditMode ? 'Update Tenant' : 'Add Tenant'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddTenantForm;